import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Divider,
    IconButton,
    InputAdornment,
    List,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import {Add, Check, Close, FilterAlt, Search, Transform} from '@mui/icons-material';
import {FabricObject} from 'fabric';
import {FILTER_KINDS, useListPanelFilters} from '../hooks/useListPanelFilters';
import {ElementListItem, TABLE_LIST_ITEM_COLUMNS} from './ElementListItem';
import {useFloorplanContext} from '../hooks/useFloorplanContext';
import {buildSeatConfigForCapacity, getTableCapacity, rebuildElement} from '../utils/fabric-factory';
import {isFloorplanTableObject} from '../utils/fabric-table-object';
import {buildNormalizedItemFromObject} from '../utils/resize-utils';

interface Props {
    elements: FabricObject[];
    selectedElements: FabricObject[];
    editingElementId: string | null;
    onSelectElement: (element: FabricObject, shiftKey: boolean) => void;
    onActivateElement: (element: FabricObject) => void;
    onEditElement: (elementId: string | null) => void;
    onElementUpdated: () => void;
    onOpenAddPanel: () => void;
    onOpenGroupsPanel: () => void;
}


const EditSidebarTablesAndElements: React.FC<Props> = ({
                                                           elements,
                                                           selectedElements,
                                                           editingElementId,
                                                           onSelectElement,
                                                           onActivateElement,
                                                           onEditElement,
                                                           onElementUpdated,
                                                           onOpenAddPanel,
                                                           onOpenGroupsPanel,
                                                       }) => {
    const {t} = useTranslation();
    const {canvas, showSeatsAroundTable, updateTableElementLocally} = useFloorplanContext();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollPositionRef = useRef<number>(0);
    const [highlightElementId, setHighlightElementId] = useState<string | null>(null);

    const {
        searchQuery,
        setSearchQuery,
        sortDirection,
        setSortDirection,
        activeFilters,
        toolbarOpen,
        toggleToolbar,
        toggleFilter,
        tables,
        otherElements,
    } = useListPanelFilters(elements);

    // Restore scroll position on mount
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container && scrollPositionRef.current) {
            container.scrollTop = scrollPositionRef.current;
        }
    }, []);

    // When returning from edit, highlight the element briefly
    const prevEditingId = useRef(editingElementId);
    useEffect(() => {
        if (prevEditingId.current && !editingElementId) {
            setHighlightElementId(prevEditingId.current);
        }
        prevEditingId.current = editingElementId;
    }, [editingElementId]);

    useEffect(() => {
        if (highlightElementId) {
            const timeout = setTimeout(() => {
                setHighlightElementId(null);
            }, 1500);
            return () => clearTimeout(timeout);
        }
    }, [highlightElementId]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        scrollPositionRef.current = e.currentTarget.scrollTop;
    }, []);

    const handleEditFromList = useCallback((elementId: string, element: FabricObject) => {
        onSelectElement(element, false);
        onEditElement(elementId);
    }, [onSelectElement, onEditElement]);

    // Inline capacity edit uses the same local table seat model as the settings panel.
    const handleCapacityChange = useCallback((el: FabricObject, capacity: number) => {
        const item = el.floorPlanItem;
        if (!item?.table) return;
        if (isFloorplanTableObject(el)) {
            el.syncFloorPlanItemCanvas();
        }
        const nextElement = updateTableElementLocally(el, (currentItem) => {
            const currentTable = currentItem.table!;
            const newSeats = buildSeatConfigForCapacity(currentTable.shape, capacity, currentTable.seats);

            return {
                ...currentItem,
                table: {
                    ...currentTable,
                    seats: newSeats,
                    resource: {
                        ...currentTable.resource,
                        capacity: getTableCapacity(newSeats, capacity),
                    },
                },
            };
        });
        if (!nextElement) return;
        nextElement.canvas?.discardActiveObject();
        onElementUpdated();
    }, [onElementUpdated, updateTableElementLocally]);

    const handleUnlockElement = useCallback((el: FabricObject) => {
        if (!canvas) return;

        let nextElement: FabricObject | null = null;

        if (el.floorPlanItem?.table) {
            nextElement = updateTableElementLocally(el, (currentItem) => ({
                ...currentItem,
                table: {
                    ...currentItem.table!,
                    canvas: {
                        ...currentItem.table!.canvas,
                        selectable: true,
                    } as any,
                },
            }));
        } else {
            const normalizedItem = buildNormalizedItemFromObject(el);
            if (!normalizedItem?.element) return;

            nextElement = rebuildElement(el, canvas, {
                ...normalizedItem,
                element: {
                    ...normalizedItem.element,
                    canvas: {
                        ...normalizedItem.element.canvas,
                        selectable: true,
                    },
                },
            }, showSeatsAroundTable);
        }

        if (!nextElement) return;

        onActivateElement(nextElement);
        onElementUpdated();
    }, [canvas, onActivateElement, onElementUpdated, showSeatsAroundTable, updateTableElementLocally]);

    const filterLabels: Record<string, string> = {
        table: t('Tables'),
        shape: t('Shapes'),
        text: t('Text'),
        wall: t('Walls'),
    };

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <Box>
                <Box sx={{p: 2, pb: 1}}>
                    <Stack flexDirection={"row"}>
                        <Stack flexDirection={"row"}>
                            <Box sx={{mr: 1}}>
                                <Button
                                    variant="text"
                                    onClick={onOpenAddPanel}
                                    startIcon={<Add fontSize="small"/>}
                                >
                                    {t('New')}
                                </Button>
                            </Box>

                            <Divider orientation="vertical"/>

                            <Box sx={{ml: 1}}>
                                <Button
                                    variant="text"
                                    onClick={onOpenGroupsPanel}
                                    startIcon={<Transform fontSize="small"/>}
                                >
                                    {t('Table Combinations')}
                                </Button>
                            </Box>
                        </Stack>

                        <Box sx={{ml: "auto"}}>
                            <IconButton
                                onClick={toggleToolbar}
                                color={toolbarOpen ? 'primary' : 'default'}
                                sx={toolbarOpen ? {
                                    bgcolor: 'primary.main',
                                    color: 'primary.contrastText',
                                    '&:hover': {bgcolor: 'primary.dark'},
                                } : undefined}
                            >
                                <FilterAlt fontSize="small"/>
                            </IconButton>
                        </Box>

                    </Stack>
                </Box>
                <Collapse in={toolbarOpen}>
                    <Stack spacing={1} sx={{px: 2, pb: 1.5}}>
                        <TextField
                            size="small"
                            placeholder={t('Search elements...')}
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onFocus={(event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                event.currentTarget.select();
                            }}
                            InputProps={{
                                sx: {height: 32, fontSize: '0.8125rem'},
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search sx={{fontSize: 18}}/>
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery ? (
                                    <InputAdornment position="end">
                                        <IconButton size="small"
                                                    onClick={() => setSearchQuery('')}
                                                    edge="end">
                                            <Close sx={{fontSize: 16}}/>
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Stack direction="row" spacing={0.5} alignItems="center"
                               flexWrap="wrap" useFlexGap>
                            <Chip
                                label={sortDirection === 'asc' ? 'A → Z' : 'Z → A'}
                                size="small"
                                variant="outlined"
                                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                            />
                            <Box sx={{width: '1px', height: 20, bgcolor: 'divider', mx: 0.5}}/>
                            {FILTER_KINDS.map(kind => (
                                <Chip
                                    key={kind}
                                    deleteIcon={<Check sx={{fontSize: 12, color: 'inherit !important'}}/>}
                                    onDelete={activeFilters.has(kind) ? () => toggleFilter(kind) : undefined}
                                    label={filterLabels[kind]}
                                    size="small"
                                    variant={activeFilters.has(kind) ? 'filled' : 'outlined'}
                                    color={activeFilters.has(kind) ? 'primary' : 'default'}
                                    onClick={() => toggleFilter(kind)}
                                />
                            ))}
                        </Stack>
                    </Stack>
                </Collapse>
            </Box>

            <Box ref={scrollContainerRef} onScroll={handleScroll}
                 sx={{
                     flex: 1,
                     overflowY: 'auto',
                     overflowX: 'hidden',
                     background: `
                         linear-gradient(#fff 30%, transparent) center top / 100% 40px,
                         radial-gradient(farthest-side at 50% 0, rgba(0,0,0,.15), transparent) center top / 100% 6px
                     `,
                     backgroundRepeat: 'no-repeat',
                     backgroundAttachment: 'local, scroll',
                     bgcolor: '#fff',
                 }}>
                {elements.length === 0 ? (
                    <Box
                        sx={{
                            px: 3,
                            py: 6,
                            textAlign: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Typography variant="body1" fontWeight={600}>
                            {t('No tables or elements yet')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {t('Use the add button above to place your first table, shape, wall, or text element.')}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {tables.length > 0 ? (
                            <>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: `minmax(0, 1fr) ${TABLE_LIST_ITEM_COLUMNS.capacity}px ${TABLE_LIST_ITEM_COLUMNS.action}px`,
                                        columnGap: `${TABLE_LIST_ITEM_COLUMNS.gap}px`,
                                        alignItems: 'end',
                                        px: 2,
                                        pt: 1.5,
                                        pb: 0.5,
                                    }}
                                >
                                    <Typography variant="body1">
                                        {t('Tables')} ({tables.length})
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{textAlign: 'center'}}>
                                        {t('Capacity')}
                                    </Typography>
                                    <Box/>
                                </Box>
                                <List dense disablePadding>
                                    {tables.map(el => (
                                        <ElementListItem
                                            key={el.floorPlanItem?.id}
                                            element={el}
                                            isSelected={selectedElements.includes(el)}
                                            isHighlighted={el.floorPlanItem?.id === highlightElementId}
                                            onSelect={onSelectElement}
                                            onActivate={onActivateElement}
                                            onUnlock={handleUnlockElement}
                                            onEdit={handleEditFromList}
                                            onCapacityChange={handleCapacityChange}
                                        />
                                    ))}
                                </List>
                            </>
                        ) : (
                            <>
                                <Typography variant="body1" sx={{px: 2, pt: 1.5, pb: 0.5}}>
                                    {t('Tables')} (0)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{px: 2, py: 1}}>
                                    {t('No tables found')}
                                </Typography>
                            </>
                        )}

                        <Typography variant="body1" sx={{px: 2, pt: 1.5, pb: 0.5}}>
                            {t('Elements')} ({otherElements.length})
                        </Typography>
                        {otherElements.length > 0 ? (
                            <List dense disablePadding>
                                {otherElements.map(el => (
                                    <ElementListItem
                                        key={el.floorPlanItem?.id}
                                        element={el}
                                        isSelected={selectedElements.includes(el)}
                                        isHighlighted={el.floorPlanItem?.id === highlightElementId}
                                        onSelect={onSelectElement}
                                        onActivate={onActivateElement}
                                        onUnlock={handleUnlockElement}
                                        onEdit={handleEditFromList}
                                        onCapacityChange={handleCapacityChange}
                                    />
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{px: 2, py: 1}}>
                                {t('No elements found')}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default EditSidebarTablesAndElements;
